import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createBookingsApi } from "../api";
import { bookingStatusTone, formatLabel, holdStatusTone } from "../labels";
import { BookingOverviewPanel } from "../components/BookingOverviewPanel";
import type {
  BookingOverview,
  BookingDetailResponse,
  HoldDetailResponse,
  ParticipantAction,
  ParticipantDto,
} from "../types";
import {
  AddParticipantDialog,
  BookingActionDialog,
  ConvertHoldDialog,
  EditBookingDialog,
  HoldActionDialog,
  ReasonActionDialog,
} from "../components/BookingDialogs";

export function BookingDetailPage() {
  const { bookingId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createBookingsApi(client), [client]);
  const toast = useToast();

  const [detail, setDetail] = useState<BookingDetailResponse | null>(null);
  const [overview, setOverview] = useState<BookingOverview | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [participantOpen, setParticipantOpen] = useState(false);
  const [participantAction, setParticipantAction] = useState<{
    id: string;
    action: ParticipantAction;
  } | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Bookings", to: "/app/bookings" },
    { label: detail?.booking.display_code ?? "Booking Detail" },
  ]);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    const [detailResult, overviewResult] = await Promise.allSettled([
      api.getDetail(bookingId),
      api.overview(bookingId),
    ]);
    if (detailResult.status === "fulfilled") {
      setDetail(detailResult.value);
    } else {
      setError(apiErrorMessage(detailResult.reason));
      setDetail(null);
    }
    if (overviewResult.status === "fulfilled") {
      setOverview(overviewResult.value.overview);
      setOverviewError(null);
    } else {
      setOverview(null);
      setOverviewError(apiErrorMessage(overviewResult.reason));
    }
    setLoading(false);
  }, [api, bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading Booking…" />;
  if (error || !detail) {
    return (
      <EmptyState
        title="Booking unavailable"
        description={error ?? "Booking not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { booking, participants, hold } = detail;

  return (
    <Stack gap={24}>
      <SectionHeader
        title={booking.display_code ? `Booking ${booking.display_code}` : "Booking"}
        description={overview ? [overview.customer.name, overview.business.name].filter(Boolean).join(" at ") || undefined : undefined}
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(`/app/businesses/${booking.business_id}`)
              }
            >
              Open Business
            </Button>
            {booking.bookable_resource_id ? (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate(
                    `/app/businesses/resources/${booking.bookable_resource_id}`,
                  )
                }
              >
                Open Resource
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              Edit window
            </Button>
            <Button variant="secondary" onClick={() => setStatusOpen(true)}>
              Status actions
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <StatusChip tone={bookingStatusTone(booking.status)}>
            Status: {formatLabel(booking.status)}
          </StatusChip>
        }
      />

      {overviewError ? (
        <Alert tone="warning" title="Names, payments and check-in history could not be loaded">
          {overviewError}
        </Alert>
      ) : null}
      {overview ? (
        <BookingOverviewPanel
          booking={booking}
          overview={overview}
          resetBusy={resetBusy}
          resetError={resetError}
          onResetTicket={async () => {
            setResetBusy(true);
            setResetError(null);
            try {
              await api.resetTicket(booking.id);
              toast.push({ title: "Ticket QR reset", tone: "success" });
              await load();
              return true;
            } catch (err) {
              setResetError(apiErrorMessage(err));
              return false;
            } finally {
              setResetBusy(false);
            }
          }}
        />
      ) : null}

      <Card
        header={
          <Text role="title" as="h3">
            Reservation
          </Text>
        }
      >
        <Stack gap={8}>
          <Text role="caption" color="muted" as="p">
            Internal identifiers, for engineering: booking {booking.id} · reference {booking.public_id} · customer {booking.customer_user_id}
          </Text>
          <Text role="body" as="p">
            Starts: {booking.starts_at}
          </Text>
          <Text role="body" as="p">
            Ends: {booking.ends_at}
          </Text>
          <Text role="body" as="p">
            Timezone: {booking.booking_timezone}
          </Text>
          <Text role="body" as="p">
            Currency: {booking.currency}
          </Text>
          <Text role="body" as="p">
            Staff: {booking.staff_user_id ?? "—"}
          </Text>
        </Stack>
      </Card>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Linked Hold
            </Text>
            {hold ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void navigate(`/app/bookings/holds/${hold.id}`)}
              >
                Open Hold
              </Button>
            ) : null}
          </Flex>
          {hold ? (
            <Text role="body" as="p">
              {hold.public_id} · {formatLabel(hold.status)} · expires{" "}
              {hold.expires_at}
            </Text>
          ) : (
            <EmptyState
              title="No linked Hold"
              description="This Booking was created without an attached allocation Hold, or the Hold was already released."
              reason="none"
            />
          )}
        </Stack>
      </Surface>

      <Surface>
        <Stack gap={16}>
          <Flex justify="space-between" align="center" wrap gap={8}>
            <Text role="h2" as="h2">
              Participants
            </Text>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setParticipantOpen(true)}
            >
              Add participant
            </Button>
          </Flex>
          {participants.length === 0 ? (
            <EmptyState title="No participants" reason="none" />
          ) : (
            <ParticipantsTable
              rows={participants}
              onAction={(id, action) => setParticipantAction({ id, action })}
            />
          )}
        </Stack>
      </Surface>

      <BookingActionDialog
        open={statusOpen}
        onClose={() => {
          setStatusOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyAction(booking.id, input.action, {
              ...(input.reason ? { reason: input.reason } : {}),
            });
            toast.push({
              title: "Booking action applied",
              description: formatLabel(input.action),
              tone: "success",
            });
            setStatusOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <EditBookingDialog
        open={editOpen}
        startsAt={booking.starts_at}
        endsAt={booking.ends_at}
        staffUserId={booking.staff_user_id ?? ""}
        currency={booking.currency}
        onClose={() => {
          setEditOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (patch) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.update(booking.id, patch);
            toast.push({ title: "Booking updated", tone: "success" });
            setEditOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <AddParticipantDialog
        open={participantOpen}
        onClose={() => {
          setParticipantOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.addParticipant(booking.id, input);
            toast.push({ title: "Participant added", tone: "success" });
            setParticipantOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ReasonActionDialog
        open={participantAction !== null}
        title={`${formatLabel(participantAction?.action ?? "action")} participant`}
        onClose={() => {
          setParticipantAction(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (reason) => {
          if (!participantAction) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.applyParticipantAction(
              booking.id,
              participantAction.id,
              participantAction.action,
              reason ? { reason } : {},
            );
            toast.push({
              title: "Participant updated",
              description: formatLabel(participantAction.action),
              tone: "success",
            });
            setParticipantAction(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

export function HoldDetailPage() {
  const { holdId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createBookingsApi(client), [client]);
  const toast = useToast();

  const [detail, setDetail] = useState<HoldDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Bookings", to: "/app/bookings" },
    { label: "Holds", to: "/app/bookings/holds" },
    { label: detail?.hold.public_id ?? "Hold Detail" },
  ]);

  const load = useCallback(async () => {
    if (!holdId) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await api.getHold(holdId));
    } catch (err) {
      setError(apiErrorMessage(err));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [api, holdId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingState message="Loading Hold…" />;
  if (error || !detail) {
    return (
      <EmptyState
        title="Hold unavailable"
        description={error ?? "Hold not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { hold } = detail;

  return (
    <Stack gap={24}>
      <SectionHeader
        title={hold.public_id}
        description={`Hold ${hold.id}`}
        actions={
          <Flex gap={8} wrap>
            <Button variant="secondary" onClick={() => setActionOpen(true)}>
              Hold actions
            </Button>
            <Button variant="secondary" onClick={() => setConvertOpen(true)}>
              Convert to Booking
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <StatusChip tone={holdStatusTone(hold.status)}>
            Status: {formatLabel(hold.status)}
          </StatusChip>
        }
      />

      <Card
        header={
          <Text role="title" as="h3">
            Allocation
          </Text>
        }
      >
        <Stack gap={8}>
          <Text role="body" as="p">
            Customer: {hold.customer_user_id}
          </Text>
          <Text role="body" as="p">
            Business: {hold.business_id}
          </Text>
          <Text role="body" as="p">
            Resource: {hold.bookable_resource_id ?? "—"}
          </Text>
          <Text role="body" as="p">
            Window: {hold.starts_at} → {hold.ends_at}
          </Text>
          <Text role="body" as="p">
            Expires: {hold.expires_at}
          </Text>
          <Text role="body" as="p">
            Converted Booking: {hold.converted_booking_id ?? "—"}
          </Text>
          {hold.converted_booking_id ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                void navigate(`/app/bookings/${hold.converted_booking_id}`)
              }
            >
              Open converted Booking
            </Button>
          ) : null}
        </Stack>
      </Card>

      <HoldActionDialog
        open={actionOpen}
        onClose={() => {
          setActionOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const { action, ...body } = input;
            await api.applyHoldAction(hold.id, action, body);
            toast.push({
              title: "Hold action applied",
              description: formatLabel(action),
              tone: "success",
            });
            setActionOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ConvertHoldDialog
        open={convertOpen}
        onClose={() => {
          setConvertOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            const result = await api.convertHold(hold.id, input);
            toast.push({
              title: "Hold converted",
              description: result.booking.public_id,
              tone: "success",
            });
            setConvertOpen(false);
            void navigate(`/app/bookings/${result.booking.id}`);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

function ParticipantsTable({
  rows,
  onAction,
}: {
  rows: ParticipantDto[];
  onAction: (id: string, action: ParticipantAction) => void;
}) {
  const actions: ParticipantAction[] = [
    "confirm",
    "attend",
    "fulfill",
    "cancel",
    "remove",
  ];
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Type</Th>
          <Th>User ID</Th>
          <Th>Status</Th>
          <Th>Joined</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.participant_type)}</Td>
            <Td>{row.user_id}</Td>
            <Td>{formatLabel(row.status)}</Td>
            <Td>{row.joined_at}</Td>
            <Td>
              <Flex gap={4} wrap>
                {actions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    variant="ghost"
                    onClick={() => onAction(row.id, action)}
                  >
                    {formatLabel(action)}
                  </Button>
                ))}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
